// Step 2: Grouping
import { useState } from 'react';
import { Layers, Merge } from 'lucide-react';
import { useUploadGroups } from '../hooks/useUploadGroups';
import { UploadGroupCard } from '../components/UploadGroupCard';
import { cn } from '@utils/classNames';

export const Step2Grouping: React.FC = () => {
  const {
    groups,
    setMainFile,
    setVersionType,
    splitFile,
    moveFile,
    mergeGroups,
    getFileSize,
  } = useUploadGroups();

  const [mergeSource, setMergeSource] = useState<string | null>(null);

  const handleMergeTap = (groupId: string) => {
    if (!mergeSource) {
      setMergeSource(groupId);
      return;
    }
    if (mergeSource === groupId) {
      setMergeSource(null);
      return;
    }
    mergeGroups(mergeSource, groupId);
    setMergeSource(null);
  };

  return (
    <div className="space-y-3 sm:space-y-4 w-full">
      <div className="text-center mb-2 sm:mb-3">
        <h2 className="font-dela text-lg sm:text-xl text-white mb-1 flex items-center justify-center gap-2">
          <Layers className="w-5 h-5 text-primary" />
          Grouping
        </h2>
        <p className="text-white/60 font-inter text-sm sm:text-base">
          Files with similar names are grouped automatically. Adjust if needed.
        </p>
      </div>

      {groups.length > 1 && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-white/5 border border-white/10">
          <Merge className="w-4 h-4 text-white/50 flex-shrink-0" />
          <p className="text-white/60 text-xs sm:text-sm font-inter">
            {mergeSource
              ? 'Tap another group to merge, or tap the same group to cancel.'
              : 'Tap the merge button on a group, then tap another group to combine them.'}
          </p>
        </div>
      )}

      <div className="space-y-2 sm:space-y-3">
        {groups.map((group) => (
          <div key={group.id} className="relative">
            {groups.length > 1 && (
              <button
                type="button"
                onClick={() => handleMergeTap(group.id)}
                className={cn(
                  'absolute top-2 right-12 z-10 p-1.5 min-h-[36px] min-w-[36px] rounded-lg flex items-center justify-center transition-all',
                  mergeSource === group.id
                    ? 'bg-primary/30 text-primary border border-primary/40'
                    : 'bg-white/10 text-white/50 hover:bg-white/20'
                )}
                aria-label="Merge group"
              >
                <Merge className="w-3.5 h-3.5" />
              </button>
            )}
            <UploadGroupCard
              group={group}
              mode="grouping"
              getFileSize={getFileSize}
              onSetMainFile={(fileName) => setMainFile(group.id, fileName)}
              onSetVersionType={(fileName, type) => setVersionType(group.id, fileName, type)}
              onSplitFile={splitFile}
              onMoveFile={moveFile}
              otherGroups={groups.filter((g) => g.id !== group.id)}
            />
          </div>
        ))}
      </div>

      {groups.length === 0 && (
        <p className="text-center text-white/50 font-inter text-sm py-8">
          No files to group. Go back and select files first.
        </p>
      )}
    </div>
  );
};
